import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getTrendingPosts, getAllPosts, votePost } from '../api/postApi';
import { getCategories } from '../api/categoryApi';
import { getShowcaseItems, ShowcaseItem, deleteShowcaseItem } from '../api/photoApi';
import { openCustomAlert } from '../components/common/useCustomAlert';
import CommentSection from '../components/CommentSection';
import ReportButton from '../components/ReportButton';
import DiscoveryRail from '../components/public/DiscoveryRail';
import { HomeVideo } from '../components/public/HomeSections';
import { getHomeVideos } from '../api/videoApi';
import { cleanClaim } from '../utils/truthpickContent';
import { createBoardSearchParams } from '../utils/boardSearchParams';
import { photoAlt } from '../content/weeklyClaimLens';
import './BoardPage.css';

export default function BoardPage() {
  const [posts, setPosts] = useState<any[]>([]);
  const [photos, setPhotos] = useState<ShowcaseItem[]>([]);
  const [currentTab, setCurrentTab] = useState<'ALL' | 'TRENDING' | 'CATEGORY' | 'PHOTO'>('ALL');
  const [openedPostId, setOpenedPostId] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [categories, setCategories] = useState<any[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [categoryCounts, setCategoryCounts] = useState<Record<string, number>>({});
  const [discoveryPosts, setDiscoveryPosts] = useState<any[]>([]);
  const [discoveryPhotos, setDiscoveryPhotos] = useState<ShowcaseItem[]>([]);
  const [videos, setVideos] = useState<HomeVideo[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isCategoryOpen, setIsCategoryOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const searchKey = searchParams.toString();
  const focusedPostId = Number(searchParams.get('post')) || null;
  const focusedPhotoId = Number(searchParams.get('photo')) || null;

  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : '';
  const currentUserIdStr = typeof window !== 'undefined' ? localStorage.getItem('userId') : null;
  const isLoggedIn = !!token;

  const loadPosts = useCallback(async (tab: 'ALL' | 'TRENDING' | 'CATEGORY' | 'PHOTO', category?: string) => {
    setLoading(true);
    setLoadError('');
    try {
      if (tab === 'PHOTO') {
        const photoData = await getShowcaseItems();
        setPhotos(photoData || []);
      } else {
        let data: any[] = [];
        if (tab === 'TRENDING') {
          data = await getTrendingPosts();
        } else if (tab === 'CATEGORY' && category) {
          const source = await getAllPosts();
          data = source.filter((post: any) => post.category === category);
        } else {
          data = await getAllPosts();
        }
        setPosts(data || []);

        // 페이징 처리
        const total = Math.max(1, Math.ceil(data.length / 10));
        setCurrentPage(prev => Math.min(prev, total));
      }
    } catch (e) {
      console.error('[sohn] Data load error:', e);
      setLoadError('목록을 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.');
    } finally {
      setLoading(false);
    }
  }, []);

  // 초기 로드 및 URL 파라미터 감시
  useEffect(() => {
    const routeParams = new URLSearchParams(searchKey);
    const tabParam = routeParams.get('tab') as any;
    const pageParam = Number(routeParams.get('page') || '1');
    const categoryParam = routeParams.get('category') || '';

    const validTabs = ['ALL', 'TRENDING', 'CATEGORY', 'PHOTO'];
    const activeTab = validTabs.includes(tabParam) ? tabParam : 'ALL';

    setCurrentTab(activeTab);
    setCurrentPage(pageParam);
    setSelectedCategory(categoryParam);
    setOpenedPostId(focusedPostId || focusedPhotoId);

    loadPosts(activeTab, categoryParam || undefined);
  }, [searchKey, loadPosts, focusedPostId, focusedPhotoId]);

  const handleVote = async (postId: number, voteType: 'TRUTH' | 'LIE') => {
    if (!isLoggedIn) {
      openCustomAlert({ message: '로그인 후 이용해주세요', type: 'error' });
      return;
    }
    try {
      const result = await votePost(postId, voteType);
      openCustomAlert({ message: result, type: 'success' });
      await loadPosts(currentTab, selectedCategory || undefined);
    } catch (e: any) {
      openCustomAlert({
        message: e?.response?.data || e?.message || '오류 발생',
        type: 'error',
      });
    }
  };

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const [cats, allPosts] = await Promise.all([getCategories(), getAllPosts()]);
        setCategories(cats);
        setDiscoveryPosts(allPosts);
        const counts: Record<string, number> = {};
        allPosts.forEach((post: any) => {
          const key = (post.category || '미분류').trim();
          counts[key] = (counts[key] || 0) + 1;
        });
        setCategoryCounts(counts);
      } catch (e) {
        console.error(e);
      }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    getShowcaseItems().then((items) => setDiscoveryPhotos(items || [])).catch(() => setDiscoveryPhotos([]));
    getHomeVideos().then(setVideos).catch(() => setVideos([]));
  }, []);

  const POSTS_PER_PAGE = 10;
  const normalizedSearch = searchQuery.trim().toLocaleLowerCase();
  const filteredPosts = normalizedSearch && !focusedPostId
    ? posts.filter((post) => `${cleanClaim(post.content, 10000)} ${post.category || ''}`.toLocaleLowerCase().includes(normalizedSearch))
    : posts;
  const filteredPhotos = normalizedSearch && !focusedPhotoId
    ? photos.filter((photo) => `${photo.title || ''} ${photo.content || ''}`.toLocaleLowerCase().includes(normalizedSearch))
    : photos;
  const totalPages = Math.max(1, Math.ceil(filteredPosts.length / POSTS_PER_PAGE));
  const currentPosts = focusedPostId
    ? filteredPosts.filter((post) => post.id === focusedPostId)
    : filteredPosts.slice((currentPage - 1) * POSTS_PER_PAGE, currentPage * POSTS_PER_PAGE);
  const visiblePhotos = focusedPhotoId ? filteredPhotos.filter((photo) => photo.id === focusedPhotoId) : filteredPhotos;

  const pageGroupSize = 5;
  const currentGroupStart = Math.floor((currentPage - 1) / pageGroupSize) * pageGroupSize + 1;
  const currentGroupEnd = Math.min(currentGroupStart + pageGroupSize - 1, totalPages);

  const goToPage = (page: number) => {
    setCurrentPage(page);
    setSearchParams(createBoardSearchParams(currentTab, page, selectedCategory));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleTabChange = (tab: 'ALL' | 'TRENDING' | 'CATEGORY' | 'PHOTO') => {
    setCurrentTab(tab);
    setCurrentPage(1);
    setIsCategoryOpen(false);
    setSearchQuery('');
    if (tab !== 'CATEGORY') setSelectedCategory('');
    setSearchParams(createBoardSearchParams(tab, 1, selectedCategory));
  };

  const handleCategorySelect = (name: string) => {
    setSelectedCategory(name);
    setIsCategoryOpen(false);
    setCurrentTab('CATEGORY');
    setCurrentPage(1);
    setSearchParams(createBoardSearchParams('CATEGORY', 1, name));
  };

  const handleDeletePhoto = (id: number) => {
      openCustomAlert({
          message: '사진을 삭제하시겠습니까?',
          type: 'confirm',
          onConfirm: async () => {
              try {
                  await deleteShowcaseItem(id);
                  openCustomAlert({ message: '삭제되었습니다.', type: 'success' });
                  loadPosts('PHOTO');
              } catch (e) {
                  console.error(e);
                  openCustomAlert({ message: '삭제 실패', type: 'error' });
              }
          }
      });
  };

  const goToCreate = () => {
    if (!isLoggedIn) {
      openCustomAlert({ message: '로그인 후 이용해주세요', type: 'error' });
      return;
    }
    if (currentTab === 'PHOTO') {
      navigate('/create-photo');
      return;
    }
    navigate('/create', { state: { from: '/board' } });
  };

  return (
    <main className="board-page">
      <header className="board-masthead">
        <div>
          <span className="board-eyebrow">EXPLORE / TRUTHPICK</span>
          <h1>{currentTab === 'PHOTO' ? '사진은 무엇을 드러낼까요?' : '무엇이 진실을 덮고 있을까요?'}</h1>
          <p>주장을 열어보고, 근거를 묻고, 서로의 판단을 확인하세요.</p>
          {currentTab === 'PHOTO' && !focusedPhotoId && <a className="board-observation-link" href="#/observation">사진 한 장을 깊이 보는 관찰실 ↗</a>}
          {(focusedPostId || focusedPhotoId) && <a className="board-back-link" href={focusedPhotoId ? '#/board?tab=PHOTO' : '#/board'}>← 전체 이야기로 돌아가기</a>}
        </div>
        <button className="board-create-action" type="button" onClick={goToCreate}>
          {currentTab === 'PHOTO' ? '사진 올리기' : '새 질문 올리기'}
        </button>
      </header>

      <div className="board-overview" aria-label="커뮤니티 현황">
        <div><strong>{discoveryPosts.length.toLocaleString('ko-KR')}</strong><span>쌓인 이야기</span></div>
        <div><strong>{discoveryPosts.reduce((total, post) => total + (post.commentCount || 0), 0).toLocaleString('ko-KR')}</strong><span>이어진 의견</span></div>
        <div><strong>{discoveryPhotos.length.toLocaleString('ko-KR')}</strong><span>공유된 사진</span></div>
        <p>지금 궁금한 주제에서 시작해 보세요. 모든 숫자는 실제 커뮤니티 데이터입니다.</p>
      </div>

      <nav className="board-filters" aria-label="발견 필터">
        <button
          type="button"
          className={currentTab === 'ALL' ? 'control-button active' : 'control-button'}
          onClick={() => handleTabChange('ALL')}
          aria-pressed={currentTab === 'ALL'}
        >
          최신
        </button>

        <button
          type="button"
          className={currentTab === 'TRENDING' ? 'control-button active' : 'control-button'}
          onClick={() => handleTabChange('TRENDING')}
          aria-pressed={currentTab === 'TRENDING'}
        >
          인기
        </button>

        <button
            type="button"
            className={currentTab === 'PHOTO' ? 'control-button active' : 'control-button'}
            onClick={() => handleTabChange('PHOTO')}
            aria-pressed={currentTab === 'PHOTO'}
        >
            사진
        </button>

        <button
          type="button"
          className={currentTab === 'CATEGORY' ? 'control-button active' : 'control-button'}
          onClick={() => handleTabChange('CATEGORY')}
          aria-pressed={currentTab === 'CATEGORY'}
        >
          주제
        </button>

        {currentTab === 'CATEGORY' && (
          <div className="category-select-wrap">
            <button
              type="button"
              className="category-select"
              aria-haspopup="listbox"
              aria-expanded={isCategoryOpen}
              onClick={() => setIsCategoryOpen((prev) => !prev)}
            >
              <span className={selectedCategory ? '' : 'category-placeholder'}>
                {selectedCategory || '카테고리 선택'}
              </span>
              <span className={`category-arrow ${isCategoryOpen ? 'open' : ''}`} aria-hidden="true">⌄</span>
            </button>

            {isCategoryOpen && (
              <div className="category-dropdown" role="listbox" aria-label="주제 선택">
                {categories.map((item) => (
                  <button
                    type="button"
                    key={item.id}
                    role="option"
                    aria-selected={selectedCategory === item.name}
                    className={`category-option ${selectedCategory === item.name ? 'selected' : ''}`}
                    onClick={() => handleCategorySelect(item.name)}
                  >
                    <span className="category-option-label">{item.name}</span>
                    <span className="category-option-count">{categoryCounts[item.name] ?? 0}</span>
                    {selectedCategory === item.name && <span className="category-check" aria-hidden="true">✓</span>}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </nav>

      <div className="board-content-layout">
      <div className="board-primary-column">
        {!focusedPostId && !focusedPhotoId && <div className="board-list-toolbar">
          <div><strong>{currentTab === 'PHOTO' ? '사진 이야기' : selectedCategory || (currentTab === 'TRENDING' ? '인기 이야기' : '새로운 이야기')}</strong><span>{currentTab === 'PHOTO' ? filteredPhotos.length : filteredPosts.length}개 콘텐츠</span></div>
          <label className="board-search"><span className="sr-only">{currentTab === 'PHOTO' ? '사진 검색' : '이야기 검색'}</span><input type="search" value={searchQuery} onChange={(event) => { setSearchQuery(event.target.value); setCurrentPage(1); }} placeholder={currentTab === 'PHOTO' ? '사진 제목과 설명 검색' : '제목과 내용 검색'} /></label>
        </div>}

      <section className={`board-feed ${currentTab === 'PHOTO' ? 'photo-mode' : ''}`} aria-live="polite">
        {loading ? (
          <div className="board-status"><span className="board-loading-mark" aria-hidden="true" /><p>주장을 불러오는 중...</p></div>
        ) : loadError ? (
          <div className="board-status board-error" role="alert">
            <p>{loadError}</p>
            <button type="button" onClick={() => loadPosts(currentTab, selectedCategory || undefined)}>다시 시도</button>
          </div>
        ) : currentTab === 'PHOTO' ? (
            <div className={`photo-list ${focusedPhotoId ? 'photo-focused' : ''}`}>
                {visiblePhotos.map((photoItem) => (
                    <article className="photo-card" key={photoItem.id}>
                        <div className="photo-image-wrapper">
                            <img
                                src={photoItem.imageUrl}
                                alt={photoAlt(photoItem)}
                                className="photo-image"
                            />
                            {String(photoItem.userId) === String(currentUserIdStr) && currentUserIdStr && (
                                    <button
                                        type="button"
                                        className="photo-delete-btn"
                                        aria-label={`${photoItem.title || '사진'} 삭제`}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleDeletePhoto(photoItem.id);
                                    }}
                                >
                                    삭제
                                </button>
                            )}
                    </div>
                        <div className="photo-story"><h2>{photoItem.title || `사진 #${photoItem.id}`}</h2><p>{photoItem.content || '사진에서 무엇이 보이나요? 의견을 나눠보세요.'}</p></div>
                        <button
                            type="button"
                            className="photo-info-bar"
                            aria-expanded={openedPostId === photoItem.id}
                            onClick={() => setOpenedPostId(openedPostId === photoItem.id ? null : photoItem.id)}
                        >
                            <span className="comment-count-label">의견 {photoItem.commentCount || 0}</span>
                            <span className={`toggle-v-arrow ${openedPostId === photoItem.id ? 'active' : ''}`} aria-hidden="true">⌄</span>
                        </button>
                        {openedPostId === photoItem.id && (
                            <div className="photo-comments">
                                <CommentSection
                                    postId={photoItem.id}
                                    type="showcase"
                                    onCommentAdded={() => loadPosts('PHOTO')}
                                />
                            </div>
                        )}
                    </article>
                ))}
                {visiblePhotos.length === 0 && <p className="empty-message">등록된 사진이 없습니다.</p>}
            </div>
        ) : currentPosts.length > 0 ? (
            currentPosts.map((post: any, index: number) => (
              <article className="board-card" key={post.id}>
                <div className="card-header">
                  <span className="board-index">{String((currentPage - 1) * POSTS_PER_PAGE + index + 1).padStart(2, '0')}</span>
                  {post.category && (
                    <span className="post-category">
                      <span className="post-category-label">분류: </span>
                      {post.category}
                    </span>
                  )}

                  <button
                    type="button"
                    className="floating-discussion-button"
                    aria-expanded={openedPostId === post.id}
                    onClick={() =>
                      setOpenedPostId(openedPostId === post.id ? null : post.id)
                    }
                  >
                    {openedPostId === post.id ? '토론 닫기' : '토론 열기'}
                  </button>
                </div>

                <div
                  className="board-content"
                  dangerouslySetInnerHTML={{ __html: post.content }}
                />

                <div className="board-stats">
                  진실 {post.truthVoteCount} · 거짓 {post.lieVoteCount} · 댓글 {post.commentCount}
                </div>

                <div className="card-action-bar">
                  <button
                    type="button"
                    className="action-button"
                    onClick={() => handleVote(post.id, 'TRUTH')}
                  >
                    진실 <strong>{post.truthVoteCount}</strong>
                  </button>
                  <button
                    type="button"
                    className="action-button"
                    onClick={() => handleVote(post.id, 'LIE')}
                  >
                    거짓 <strong>{post.lieVoteCount}</strong>
                  </button>
                  <ReportButton targetType="POST" targetId={post.id} />
                </div>

                {openedPostId === post.id && (
                  <CommentSection postId={post.id} onCommentAdded={() => loadPosts(currentTab, selectedCategory || undefined)} />
                )}
              </article>
            ))
        ) : <p className="empty-message">아직 등록된 주장이 없습니다.</p>}
      </section>

      {currentTab !== 'PHOTO' && !focusedPostId && (
          <nav className="board-pagination" aria-label="게시물 페이지">
            {currentGroupStart > 1 && (
              <button type="button" className="pagination-button" aria-label="이전 페이지 그룹" onClick={() => goToPage(currentGroupStart - 5)}>이전</button>
            )}
            {Array.from({ length: currentGroupEnd - currentGroupStart + 1 }, (_, i) => currentGroupStart + i).map((page) => (
              <button
                type="button"
                key={page}
                className={'pagination-button ' + (currentPage === page ? 'active' : '')}
                aria-current={currentPage === page ? 'page' : undefined}
                onClick={() => goToPage(page)}
              >
                {page}
              </button>
            ))}
            {currentGroupEnd < totalPages && (
              <button type="button" className="pagination-button" aria-label="다음 페이지 그룹" onClick={() => goToPage(currentGroupEnd + 1)}>다음</button>
            )}
          </nav>
      )}
      </div>
      <DiscoveryRail posts={discoveryPosts} photos={discoveryPhotos} videos={videos} selectedCategory={selectedCategory} photoMode={currentTab === 'PHOTO'} />
      </div>
    </main>
  );
}
